import { useState } from "react";
import { useAuth } from "../../auth/useAuth";
import { RecoveryCodeModal } from "./RecoveryCodeModal";
import * as S from "./sheet";

type Tab = "info" | "password" | "danger";

export function ProfileScreen({ onClose }: { onClose: () => void }) {
  const { user, logout, updateProfile, changePassword, rotateRecovery } = useAuth();
  const [tab, setTab] = useState<Tab>("info");
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rotatedCode, setRotatedCode] = useState<string | null>(null);

  if (!user) return null;

  const saveInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      await updateProfile({
        displayName: displayName.trim() || null,
        email,
      });
      setSuccess("Profile saved.");
    } catch (err) {
      setError((err as { message?: string }).message ?? "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const updatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setSuccess("Password updated. Other devices have been signed out.");
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      setError((err as { message?: string }).message ?? "Password change failed.");
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      const code = await rotateRecovery();
      setRotatedCode(code);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Rotate failed.");
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => {
    await logout();
    onClose();
  };

  if (rotatedCode) {
    return (
      <RecoveryCodeModal
        code={rotatedCode}
        context="rotate"
        onConfirm={() => setRotatedCode(null)}
      />
    );
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.card, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <span style={S.title}>{user.displayName ?? user.username}</span>
          <button onClick={onClose} style={S.closeButton} aria-label="Close">
            ×
          </button>
        </div>

        <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <TabButton active={tab === "info"} onClick={() => { setTab("info"); setError(null); setSuccess(null); }}>
            Profile
          </TabButton>
          <TabButton active={tab === "password"} onClick={() => { setTab("password"); setError(null); setSuccess(null); }}>
            Password
          </TabButton>
          <TabButton active={tab === "danger"} onClick={() => { setTab("danger"); setError(null); setSuccess(null); }}>
            Recovery
          </TabButton>
        </div>

        {tab === "info" && (
          <form onSubmit={saveInfo} style={S.body}>
            <div style={S.field}>
              <label style={S.label}>Username</label>
              <input style={{ ...S.input, opacity: 0.6 }} type="text" value={user.username} disabled />
              <span style={{ fontSize: 12, opacity: 0.7 }}>Usernames can't be changed yet.</span>
            </div>
            <div style={S.field}>
              <label style={S.label}>Display name</label>
              <input style={S.input} type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={60} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Email</label>
              <input style={S.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            {error && <div style={S.errorText}>{error}</div>}
            {success && <div style={{ color: "#9adf9a", fontSize: 13 }}>{success}</div>}
            <button type="submit" style={{ ...S.primaryButton, opacity: busy ? 0.6 : 1 }} disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </button>
            <button type="button" onClick={doLogout} style={S.secondaryButton}>
              Log out
            </button>
          </form>
        )}

        {tab === "password" && (
          <form onSubmit={updatePassword} style={S.body}>
            <div style={S.field}>
              <label style={S.label}>Current password</label>
              <input style={S.input} type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            </div>
            <div style={S.field}>
              <label style={S.label}>New password</label>
              <input style={S.input} type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={10} required />
            </div>
            {error && <div style={S.errorText}>{error}</div>}
            {success && <div style={{ color: "#9adf9a", fontSize: 13 }}>{success}</div>}
            <button type="submit" style={{ ...S.primaryButton, opacity: busy ? 0.6 : 1 }} disabled={busy}>
              {busy ? "Updating…" : "Change password"}
            </button>
          </form>
        )}

        {tab === "danger" && (
          <div style={S.body}>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, opacity: 0.92 }}>
              Your recovery code is the only way to reset your password — there's no email reset. Rotate it if you think it's been compromised, or if you've already used it.
            </p>
            {error && <div style={S.errorText}>{error}</div>}
            <button onClick={rotate} style={{ ...S.primaryButton, opacity: busy ? 0.6 : 1 }} disabled={busy}>
              {busy ? "Generating…" : "Generate a new recovery code"}
            </button>
            <p style={{ margin: 0, fontSize: 12, opacity: 0.7 }}>
              The new code will be shown once — save it where you can find it again. Your old code will stop working immediately.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: "10px 12px",
        background: "transparent",
        color: active ? "#ffd28a" : "#c0a484",
        border: "none",
        borderBottom: active ? "2px solid #ffd28a" : "2px solid transparent",
        fontFamily: "var(--font-rune)",
        fontSize: 12,
        letterSpacing: 1,
        cursor: "pointer",
        textTransform: "uppercase",
      }}
    >
      {children}
    </button>
  );
}
